import { RefObject, useCallback, useContext } from "react";
import { CardActions } from "@remo-co/ui-core/src/components/CardActions";
import { CardContent } from "@remo-co/ui-core/src/components/CardContent";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { List } from "@remo-co/ui-core/src/components/List";
import { ListItem } from "@remo-co/ui-core/src/components/ListItem";
import { ListItemAvatar } from "@remo-co/ui-core/src/components/ListItemAvatar";
import { ListItemText } from "@remo-co/ui-core/src/components/ListItemText";
import { Popover } from "@remo-co/ui-core/src/components/Popover";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useSelector } from "react-redux";
import { DateRange as BookIcon } from "@remo-co/ui-core/src/icons/DateRange";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import { Edit as EditIcon } from "@remo-co/ui-core/src/icons/Edit";
import { Language as WebsiteIcon } from "@remo-co/ui-core/src/icons/Language";
import { LinkedIn as LinkedInIcon } from "@remo-co/ui-core/src/icons/LinkedIn";
import { Facebook as FacebookIcon } from "@remo-co/ui-core/src/icons/Facebook";
import { Chat as ChatIcon } from "@remo-co/ui-core/src/icons/Chat";
import { PersonAdd } from "@remo-co/ui-core/src/icons/PersonAdd";
import { useI18n } from "i18n";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { IUser } from "modules/app/types";
import SansanIcon from "modules/userProfile/icons/SansanIcon";
import Avatar from "modules/userAvatar/Avatar";
import { makeSelectUserRoomStatusByRoomId } from "modules/room/redux/selectors";
import { selectCurrentRoom } from "store/rooms";
import { makeSelectUserById } from "modules/event/usersInEvent/selectors";
import { isRoomFull } from "helpers/roomHelper";
import { IRoom } from "types";
import { useInviteToTable } from "modules/inviteToTable";
import logger from "logging/logger";
import {
  selectCurrentEvent,
  selectCurrentEventCompanyPlan,
} from "modules/event/selectors";
import { getUserName } from "modules/userProfile";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { selectIsAnonymousUser } from "modules/auth/redux/selectors";
import { ActivityStatusIndicator } from "modules/eventActivityStatus/components/ActivityStatusIndicator";
import ProfileCardIcon from "../ProfileCardIcon";
import { useStyles } from "./styles";

interface Props {
  open?: boolean;
  hideMessageButton?: boolean;
  data: {
    userId?: string;
    user: IUser;
    anchorEl: RefObject<HTMLElement | HTMLDivElement>;
  };
  showRequestInfo?: boolean;
  onStartChat?: (userId: string) => void;
  onEdit?: { (): void } | null;
  onClose?: () => void;
  usersTimezone?: string;
}
const UserProfileCard = ({
  open,
  hideMessageButton,
  showRequestInfo,
  data,
  onEdit,
  onClose,
  onStartChat,
  usersTimezone,
}: Props): JSX.Element => {
  const { user, anchorEl } = data;
  const {
    headline,
    meetingLink,
    company,
    linkedInProfileURL,
    facebookProfileURL,
    sansanProfileURL,
    website: websiteURL,
  } = user.profile;
  const linkedInUrl = user.linkedIn?.publicProfileUrl ?? linkedInProfileURL;
  const { t } = useI18n(["common", "eventForm", "conversation", "event"]);
  const { track } = useContext(TRACKING_CONTEXT);
  const classes = useStyles();
  const toRoom = useSelector(selectCurrentRoom);
  const toRoomId = toRoom && toRoom.id;
  const { inviteToTable } = useInviteToTable();
  const event = useSelector(selectCurrentEvent);
  const eventCompanyPlan = useSelector(selectCurrentEventCompanyPlan);
  const usersInMyRoom = useSelector(
    makeSelectUserRoomStatusByRoomId(toRoomId ?? ""),
  );
  const userInSpace = useSelector(makeSelectUserById(user.id));
  const isUserAnonymous = useSelector(selectIsAnonymousUser);
  const { addWarningNotification } = useNotificationActions();

  const weAreInSameRoom =
    usersInMyRoom && userInSpace && usersInMyRoom[userInSpace.id];

  const handleMessage = useCallback(() => {
    if (onStartChat && data?.userId) {
      onStartChat(data.userId);
      track(Events.BUSINESSCARD_MESSAGE_CLICK);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onInviteUserToRoom = (toId: string, room: IRoom) => {
    if (!room) {
      logger.warn("[onInviteUserToRoom] No Room ");
      if (onClose) onClose();
      return;
    }

    const { isFull } = isRoomFull(room, eventCompanyPlan);

    if (isFull) {
      addWarningNotification({
        message: t("table.full.message"),
      });
    } else {
      inviteToTable({ targetUserId: toId, targetTableId: room.id });
      if (onClose) onClose();
    }
  };

  const handleInviteUser = () => {
    if (userInSpace && toRoom) {
      onInviteUserToRoom(userInSpace.id, toRoom);
    }
  };

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Popover
      open={!!open}
      onClose={onClose}
      onBackdropClick={onClose}
      anchorEl={anchorEl.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Container
        data-testid="user-profile-card-ctn"
        className={classes.cardContainer}
      >
        <CardContent className={classes.cardContent}>
          <List disablePadding>
            <ListItem disableGutters style={{ alignItems: "flex-start" }}>
              <ListItemAvatar>
                <div className="cc-av-wrap">
                  <Avatar className="avatar" size="lg" user={user} />
                  {usersTimezone && (
                    <Typography
                      component="div"
                      style={{ marginTop: 5, fontSize: "0.6rem" }}
                      data-testid="user-local-time"
                    >
                      {usersTimezone}
                    </Typography>
                  )}
                </div>
              </ListItemAvatar>
              <ListItemText
                style={{ padding: "0 8px" }}
                className={classes.listItemText}
              >
                <Typography variant="h3" className={classes.userName} noWrap>
                  {getUserName(user)}
                  {onEdit && !isUserAnonymous && (
                    <IconButton
                      onClick={handleEdit}
                      className={classes.editButton}
                      size="small"
                      disableRipple
                    >
                      <EditIcon className={classes.editIcon} />
                    </IconButton>
                  )}
                </Typography>
                {event?.isUserIdDisplayed && (
                  <Typography variant="caption" component="span" noWrap>
                    {t("event:user.id", {
                      key: user.id,
                    })}
                  </Typography>
                )}
                <ActivityStatusIndicator userId={user.id} small />
                {!showRequestInfo && (
                  <>
                    <Typography variant="h5">
                      {headline || <i>{t("eventForm:headline.unavailable")}</i>}
                    </Typography>
                    <Typography variant="h6" noWrap>
                      {company || (
                        <i>{t("eventForm:company.name.unavailable")}</i>
                      )}
                    </Typography>
                  </>
                )}
              </ListItemText>
            </ListItem>
          </List>
          <IconButton
            data-dd-action-name="Close Button - User Profile Card"
            className={classes.closeButton}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </CardContent>
        <CardActions disableSpacing>
          <Container
            flex
            bottom="xs"
            justifyContent="space-between"
            className={classes.cardActions}
          >
            <Container fullWidth={false} className={classes.iconsContainer}>
              {linkedInUrl && (
                <ProfileCardIcon
                  id="user-profile-card_linked-in"
                  aria-label="Linkedin"
                  href={linkedInUrl}
                >
                  <LinkedInIcon />
                </ProfileCardIcon>
              )}
              {sansanProfileURL && (
                <ProfileCardIcon
                  id="user-profile-card_sansan"
                  aria-label="Sansan"
                  href={sansanProfileURL}
                >
                  <SansanIcon />
                </ProfileCardIcon>
              )}
              {facebookProfileURL && (
                <ProfileCardIcon
                  id="user-profile-card_facebook"
                  aria-label="Facebook"
                  href={facebookProfileURL}
                >
                  <FacebookIcon />
                </ProfileCardIcon>
              )}
              {websiteURL && (
                <ProfileCardIcon
                  id="user-profile-card_website"
                  aria-label="Website"
                  href={websiteURL}
                >
                  <WebsiteIcon />
                </ProfileCardIcon>
              )}
              {meetingLink && (
                <ProfileCardIcon
                  id="user-profile-card_book-meeting"
                  aria-label="Book a Meeting"
                  href={meetingLink}
                >
                  <BookIcon fontSize="small" />
                </ProfileCardIcon>
              )}
            </Container>
            <Box className={classes.userIconContainer}>
              {!hideMessageButton && (
                <ProfileCardIcon
                  aria-label="Send a message"
                  onClick={handleMessage}
                  disabled={false}
                  openInNewTab={false}
                >
                  <ChatIcon />
                </ProfileCardIcon>
              )}
              {!weAreInSameRoom && userInSpace && (
                <ProfileCardIcon
                  aria-label="Get user"
                  data-testid="get-user"
                  onClick={handleInviteUser}
                  disabled={false}
                  openInNewTab={false}
                  isFilled={false}
                >
                  <PersonAdd />
                  <Typography className={classes.getUser}>
                    {t("get.user")}
                  </Typography>
                </ProfileCardIcon>
              )}
            </Box>
          </Container>
        </CardActions>
      </Container>
    </Popover>
  );
};

export default UserProfileCard;
